/* eslint-disable react/prop-types */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { getAddresses } from "../components/AddressComponent/services/addressService.js";
import { useMemo } from "react";
import store from "../store";
import { setTokenExpired } from "../Redux/authSlice";
import authService from "../_services/auth.service";
import wlTokenService from "../_services/wlToken.service";
import { getCookie } from "../lib/cookies";
import {
  getCustomerFromMemory,
  setCustomerInMemory,
  removeCustomerFromMemory,
  setWhitelabelInMemory,
  subscribeSessionMemory,
} from "../lib/sharedSessionMemory";

const UserContext = createContext("");

const SELECTED_ADDRESS_KEY = "ll_selected_address_uuid";

function normalizeUserData(data) {
  if (!data) return data;
  return {
    ...data,
    telefone: data.telefone != null ? String(data.telefone) : data.telefone,
  };
}

function getPersistedAddressUuid() {
  try {
    return sessionStorage.getItem(SELECTED_ADDRESS_KEY);
  } catch (_) {
    return null;
  }
}

function persistAddressUuid(uuid) {
  try {
    if (uuid) {
      sessionStorage.setItem(SELECTED_ADDRESS_KEY, uuid);
    } else {
      sessionStorage.removeItem(SELECTED_ADDRESS_KEY);
    }
  } catch (_) {
  }
}

function resolveAddress(addresses, prev) {
  if (!addresses || addresses.length === 0) return prev;

  const savedUuid = getPersistedAddressUuid();
  if (savedUuid) {
    const saved = addresses.find((a) => a.uuid === savedUuid);
    if (saved) return saved;
  }

  if (prev && !Array.isArray(prev) && Object.keys(prev).length > 0) {
    const stillExists = addresses.find((a) => a.uuid === prev.uuid);
    if (stillExists) return prev;
  }

  return addresses[0];
}

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState([]);
  const [user, setUserRaw] = useState([]);
  const setUser = useCallback((data) => setUserRaw(normalizeUserData(data)), []);
  const [addressesData, setAddressesData] = useState([]);
  const [addressShipping, setAddressShipping] = useState([]);
  const [updateAddresses, setUpdateAddresses] = useState();
  const [updateUser, setUpdateUser] = useState();
  const [cartItemsError, setCartItemsError] = useState({});
  const [haveAddressShippingRequest, setHaveAddressShippingRequest] =
    useState(null);
  const [mainAddress, setMainAddress] = useState();
  const [mainCard, setMainCard] = useState();

  const location = useLocation();
  const currentUrl = location.pathname; //

  const companyData = useMemo(() => {
    return user?.company;
  }, [user]);

  const isPJ = useMemo(() => {
    return !!user?.company?.uuid;
  }, [user]);

  const hasValidAddressShipping = useCallback((addr) => {
    if (!addr) return false;
    if (Array.isArray(addr) && addr.length === 0) return false;
    if (typeof addr === "object" && Object.keys(addr).length === 0) return false;
    return true;
  }, []);

  async function updateUserData() {
    let clientUserData = getCustomerFromMemory();
    
    if (!clientUserData || clientUserData === "null" || clientUserData === "{}") {
      try {
        const token = getCookie("wlt");
        if (!token) {
          console.error("Token não encontrado, não é possível obter customer");
          store.dispatch(setTokenExpired(true));
          return null;
        }

        const authData = await authService.postValidateChannelDeduped();

        if (authData?.success) {
          wlTokenService.saveTokens(authData, { accessToken: token });
          authService.saveOAuthFromValidateChannelResponse(authData);
          setCustomerInMemory(authData);
          const whitelabelData =
            authData?.whitelabel ||
            authData?.data?.whitelabel ||
            authData?.data?.config?.whitelabel;

          if (whitelabelData) {
            setWhitelabelInMemory(whitelabelData);
          }
          clientUserData = authData;
          store.dispatch(setTokenExpired(false));
        } else {
          wlTokenService.clearTokens();
          authService.clearAuthData();
          removeCustomerFromMemory();
          store.dispatch(setTokenExpired(true));
          return null;
        }
      } catch (error) {
        console.error("Erro ao obter customer:", error);
        if (error.response?.status === 401) {
          console.warn("Token expirado. Sessão expirada.");
        }
        wlTokenService.clearTokens();
        authService.clearAuthData();
        removeCustomerFromMemory();
        store.dispatch(setTokenExpired(true));
        return null;
      }
    }

    try {
      const user =
        typeof clientUserData === "string"
          ? JSON.parse(clientUserData)
          : clientUserData;
      
      if (!user || !user.data) {
        console.error("Dados do customer inválidos");
        return null;
      }

      setUserData(user);
      setUser(user?.data);
      setUpdateAddresses(user?.data?.addresses);
      setAddressShipping((prev) =>
        resolveAddress(user?.data?.addresses, prev)
      );

      return user.data;
    } catch (error) {
      console.error("Erro ao processar dados do customer:", error);
      return null;
    }
  }

  const updateUserDataRef = useRef(updateUserData);
  updateUserDataRef.current = updateUserData;

  const changeAddress = useCallback((address) => {
    setHaveAddressShippingRequest(false);
    setAddressShipping(address);
    persistAddressUuid(address?.uuid || null);
    setHaveAddressShippingRequest(true);
  }, []);

  useEffect(() => {
    const refreshAddresses = async () => {
      if (!user?.cpf) return;
      const addresses = await getAddresses();
      setAddressesData(addresses);
    };

    if (updateAddresses && user?.cpf) refreshAddresses();
  }, [updateAddresses, user?.cpf]);

  useEffect(() => {
    const refreshUser = async () => {
      let clientUserData = getCustomerFromMemory();
      if (typeof clientUserData === "string") {
        clientUserData = JSON.parse(clientUserData);
      }
      setCustomerInMemory(clientUserData);
      updateUserData();
    };

    if (updateUser) refreshUser();
  }, [updateUser]);

  useEffect(() => {
    const initializeUserData = () => {
      try {
        const clientUserData = getCustomerFromMemory();
        if (clientUserData) {
          const user =
            typeof clientUserData === "string"
              ? JSON.parse(clientUserData)
              : clientUserData;
          if (user && user.data) {
            setUserData(user);
            setUser(user.data);
            setUpdateAddresses(user.data.addresses);
            setAddressShipping((prev) =>
              resolveAddress(user.data.addresses, prev)
            );
          }
        }
      } catch (error) {
        console.error("Erro ao carregar dados do usuário:", error);
      }
    };

    initializeUserData();
    const unsubscribe = subscribeSessionMemory((memory) => {
      if (!memory.customer || !memory.customer.data) return;
      setUserData(memory.customer);
      setUser(memory.customer.data);
      setUpdateAddresses(memory.customer.data.addresses);
      setAddressShipping((prev) =>
        resolveAddress(memory.customer.data.addresses, prev)
      );
    });
    return unsubscribe;
  }, [hasValidAddressShipping]);

  useEffect(() => {
    if (currentUrl.includes("/auth")) {
      return;
    }

    const state = store.getState();
    if (state?.authSlice?.tokenExpired) {
      return;
    }

    const clientUserData = getCustomerFromMemory();
    if (
      !clientUserData ||
      clientUserData === "null" ||
      clientUserData === "{}"
    ) {
      void updateUserDataRef.current();
    }
  }, [currentUrl]);

  return (
    <UserContext.Provider
      value={{
        userData,
        setUserData,
        setUser,
        user,
        updateUserData,
        changeAddress,
        addressShipping,
        addressesData,
        mainAddress,
        setMainAddress,
        setUpdateAddresses,
        setUpdateUser,
        mainCard,
        setMainCard,
        haveAddressShippingRequest,
        cartItemsError,
        setCartItemsError,
        isPJ,
        companyData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export function useUserData() {
  return useContext(UserContext);
}

UserProvider.propTypes = {
  children: PropTypes.node,
};
